// Web push for this browser, decided without the browser or the server:
// createWebPush takes both as arguments (webPush.ts binds them to this
// browser and Supabase), so what it does is tested without either
// (createWebPush.test.ts). See webPush.ts for what web push is here.

export interface SubscriptionKeys {
  endpoint: string;
  /** Unpadded base64url, as register_web_push_subscription requires. */
  p256dh: string;
  auth: string;
}

/**
 * The registration this browser made, kept across page loads: which account
 * turned notifications on here, and the endpoint registered for it. It is
 * what makes a held subscription this account's: the server is only ever
 * asked to register a subscription for the account that turned it on.
 */
export interface Registration {
  endpoint: string;
  userId: string;
}

/** What this browser can do, and the subscription it holds. */
export interface PushBrowser {
  /** Service workers, the Push API and notifications all exist here. */
  supported: boolean;
  /** Opened from the Home Screen (standalone), not in a browser tab. */
  standalone: boolean;
  /** An iPhone or iPad, where push exists only for a Home Screen install. */
  ios: boolean;
  /** This device's name in the Settings device list. */
  label: string;
  permission(): NotificationPermission;
  requestPermission(): Promise<NotificationPermission>;
  /** The site's service worker, registered and ready. */
  registration(): Promise<ServiceWorkerRegistration>;
  /** The registration this browser last made; null when none, or when the browser keeps none. */
  remembered(): Registration | null;
  /** Keep (or, with null, forget) the registration; throws when the browser refuses to keep it. */
  remember(registration: Registration | null): void;
}

export interface PushServer {
  /** The signed-in account's id; null when nobody is signed in. */
  user(): Promise<string | null>;
  /** The VAPID public key browsers subscribe with, base64url. */
  publicKey(): Promise<string>;
  /** Registers this browser for the signed-in user; returns the device id. */
  register(keys: SubscriptionKeys, label: string, replacesEndpoint: string | null): Promise<string>;
  /** Removes this browser's registration; whether it was registered. */
  unregister(endpoint: string): Promise<boolean>;
}

export type PushStatus =
  /** No push here: a browser without the Push API. */
  | 'unsupported'
  /** An iPhone or iPad in a browser tab: push works only once the site is on the Home Screen and opened from there. */
  | 'needs-home-screen'
  /** The person blocked notifications for the site in the browser's settings. */
  | 'blocked'
  | 'off'
  | 'on';

export function toBase64url(bytes: ArrayBuffer | null): string {
  if (!bytes) throw new Error('the subscription has no key');
  let binary = '';
  for (const byte of new Uint8Array(bytes)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64url(text: string): Uint8Array {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4);
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

const keysOf = (subscription: PushSubscription): SubscriptionKeys => ({
  endpoint: subscription.endpoint,
  p256dh: toBase64url(subscription.getKey('p256dh')),
  auth: toBase64url(subscription.getKey('auth')),
});

export function createWebPush(browser: PushBrowser, server: PushServer) {
  const held = async (registration: ServiceWorkerRegistration) => registration.pushManager.getSubscription();

  /** Whether the subscription was made with `serverKey` (a browser that does not report its key is taken at its word). */
  const madeWith = (subscription: PushSubscription, serverKey: Uint8Array) =>
    !subscription.options.applicationServerKey || toBase64url(subscription.options.applicationServerKey) === toBase64url(serverKey.buffer as ArrayBuffer);

  /**
   * The subscription this browser will be registered with: the one it holds
   * when that was made with the server's key, else a new one. A subscription
   * made with another key (a key the server no longer holds) is dropped
   * first: its push service refuses every push signed with the current key,
   * and the browser refuses to subscribe over it.
   */
  const subscribedWithServerKey = async (registration: ServiceWorkerRegistration, current: PushSubscription | null): Promise<PushSubscription> => {
    const serverKey = fromBase64url(await server.publicKey());
    if (current && madeWith(current, serverKey)) return current;
    if (current) await current.unsubscribe();
    return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKey.buffer as ArrayBuffer });
  };

  /** Register `subscription` as this browser for `userId`, carrying over the endpoint this browser registered for them before, and remember it. */
  const register = async (subscription: PushSubscription, userId: string) => {
    const keys = keysOf(subscription);
    const previous = browser.remembered();
    const replaces = previous && previous.userId === userId && previous.endpoint !== keys.endpoint ? previous.endpoint : null;
    await server.register(keys, browser.label, replaces);
    browser.remember({ endpoint: keys.endpoint, userId });
  };

  const signedIn = async () => {
    const user = await server.user();
    if (!user) throw new Error('you are signed out; sign in and try again');
    return user;
  };

  return {
    /**
     * On when the browser holds a live subscription that the signed-in
     * account turned on here: the remembered registration is theirs. Another
     * account's subscription, one nobody remembers turning on, or a signed-out
     * page reads off; sync() registers nothing for them, and only turnOn()
     * makes the browser this account's.
     */
    async status(): Promise<PushStatus> {
      if (!browser.supported) return browser.ios && !browser.standalone ? 'needs-home-screen' : 'unsupported';
      if (browser.permission() === 'denied') return 'blocked';
      const [user, current] = await Promise.all([server.user(), browser.registration().then(held)]);
      return current && user && browser.remembered()?.userId === user ? 'on' : 'off';
    },

    /**
     * Ask for permission, subscribe with the server's key and register this
     * browser for the signed-in account. The permission prompt is the first
     * thing, before any await: Safari on iOS shows it only while the tap
     * that asked is still the running event.
     */
    async turnOn(): Promise<void> {
      if (!browser.supported) throw new Error('this browser cannot receive notifications');
      const permission = browser.requestPermission();
      const registration = await browser.registration();
      if ((await permission) !== 'granted') {
        throw new Error('notifications are not allowed for cerebrodiem.com in this browser');
      }
      const user = await signedIn();
      const current = await subscribedWithServerKey(registration, await held(registration));
      try {
        await register(current, user);
      } catch (error) {
        // Not registered (or not remembered) means not on: nothing would be sent to it.
        await current.unsubscribe();
        throw error;
      }
    },

    /**
     * Stop this browser receiving notifications: remove its registration
     * (the endpoint it holds and the one it remembers registering, when the
     * push service replaced that), drop its subscription, and forget the
     * registration. Each step is taken whatever the ones before it did, so a
     * server that refuses still leaves a browser nothing reaches (its push
     * service answers 410 for the dropped subscription, and the row goes);
     * the first refusal is thrown once everything possible was done.
     */
    async turnOff(): Promise<void> {
      const current = browser.supported && browser.permission() === 'granted' ? await browser.registration().then(held) : null;
      const previous = browser.remembered();
      let refused: unknown = null;
      const attempt = async (step: () => Promise<unknown>) => {
        try {
          await step();
        } catch (error) {
          refused ??= error;
        }
      };
      for (const endpoint of new Set([current?.endpoint, previous?.endpoint])) {
        if (endpoint) await attempt(() => server.unregister(endpoint));
      }
      if (current) await attempt(() => current.unsubscribe());
      browser.remember(null);
      if (refused !== null) throw refused;
    },

    /**
     * Register the subscription this browser holds again, for the account
     * that turned it on here and only while they are the one signed in: it
     * refreshes the device's last-seen time, and after the push service
     * replaced the subscription (pushsubscriptionchange: the service worker
     * subscribes again and tells the page, and the page runs this) it moves
     * the device to the new endpoint, named as replacing the one last
     * registered. A subscription made with a key the server no longer holds
     * is replaced first, so a rotated key never leaves a device that reads
     * on unreachable. Nothing happens without a session, for another
     * account's subscription, or when the browser holds none.
     */
    async sync(): Promise<void> {
      if (!browser.supported || browser.permission() !== 'granted') return;
      const user = await server.user();
      if (!user || browser.remembered()?.userId !== user) return;
      const registration = await browser.registration();
      const current = await held(registration);
      if (!current) return;
      await register(await subscribedWithServerKey(registration, current), user);
    },
  };
}

export type WebPush = ReturnType<typeof createWebPush>;

const LABEL_MAX = 80;

/** This browser's name in the Settings device list: "iPhone (Home Screen)", "Chrome on Windows". */
export function deviceLabel(userAgent: string, ios: boolean, standalone: boolean): string {
  if (ios) return `${/iPad/.test(userAgent) ? 'iPad' : 'iPhone'}${standalone ? ' (Home Screen)' : ''}`;
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Firefox\//.test(userAgent)
      ? 'Firefox'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : 'Browser';
  const os = /Windows/.test(userAgent)
    ? 'Windows'
    : /Android/.test(userAgent)
      ? 'Android'
      : /Mac OS X/.test(userAgent)
        ? 'Mac'
        : /CrOS/.test(userAgent)
          ? 'ChromeOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null;
  return `${browser}${os ? ` on ${os}` : ''}${standalone ? ' (installed)' : ''}`.slice(0, LABEL_MAX);
}
